import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import SbSkillsBridgerHRV2 from "./SbSkillsBridgerHRV2";
import SkillsBridgerPageHeader from "./SkillsBridgerPageHeader";
import { ROUTE, TEXT } from "./SkillsBridgerV2.constants";
import {
  SkillsBridgerContainerStyled,
  SkillsBridgerTableAreaStyled,
} from "./SkillsBridgerV2.styles";
import { fetchAllEmployees } from "Api/apiService";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const ANALYTICS_FROM = "skills_bridger";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.SKILLS_BRIDGER.DASHBOARD_VIEWED;

const SkillsBridgerV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();

  const [loading,           setLoading]           = useState(false);
  const [employeeData,      setEmployeeData]       = useState([]);
  const [selectedEmployees, setSelectedEmployees]  = useState([]);
  const [visibleEmployees,  setVisibleEmployees]   = useState([]);

  // ── Data ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    fetchAllEmployees()
      .then((response) => {
        const employees = response?.employees ?? [];
        setEmployeeData(employees);
        // Initialise visible employees to the full dataset. The table's
        // onChange handler keeps this in sync as column filters are applied.
        setVisibleEmployees(employees);
      })
      .catch((err) => {
        console.error("Error fetching skills bridger data:", err);
        setEmployeeData([]);
        setVisibleEmployees([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Validation ─────────────────────────────────────────────────────────────
  // Group analysis is only valid when all selected employees share the same
  // Job (jd_sb_unique_id). Mixed JDs produce misleading aggregated
  // results, so we block navigation and show a warning modal instead.

  const haveSelectedEmployeesSameJob = useCallback((employees) => {
    if (employees.length <= 1) return true;
    const referenceJdSbUniqueId = employees[0]?.jd_sb_unique_id;
    return employees.every((emp) => emp?.jd_sb_unique_id === referenceJdSbUniqueId);
  }, []);

  const showJobMismatchWarning = useCallback(() => {
    Modal.warning({
      title:   TEXT.JD_MISMATCH_TITLE,
      content: TEXT.JD_MISMATCH_DESC,
    });
  }, []);

  // ── Effective selection ────────────────────────────────────────────────────
  // When column filters are active the table shows a subset of all employees.
  // We intersect the user's selection with the currently visible rows so that
  // "select all → filter by job code → Group Skills Map" navigates with only
  // the filtered-visible employees, not the full pre-filter selection.

  const getEffectiveSelection = useCallback(() => {
    if (!visibleEmployees.length) return selectedEmployees;
    const visibleIds = new Set(visibleEmployees.map((e) => e.employee_id));
    return selectedEmployees.filter((e) => visibleIds.has(e.employee_id));
  }, [selectedEmployees, visibleEmployees]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  // Navigate immediately — each destination page fetches and shows its own loader.

  const handleGroupSkillsMap = useCallback(() => {
    const effectiveEmployees = getEffectiveSelection();
    if (!haveSelectedEmployeesSameJob(effectiveEmployees)) {
      showJobMismatchWarning();
      return;
    }
    navigate(ROUTE.GROUP_SKILLS_MAP, { state: { selectedEmployees: effectiveEmployees } });
  }, [navigate, getEffectiveSelection, haveSelectedEmployeesSameJob, showJobMismatchWarning]);

  const handleGroupSkillsProficiency = useCallback(() => {
    const effectiveEmployees = getEffectiveSelection();
    if (!haveSelectedEmployeesSameJob(effectiveEmployees)) {
      showJobMismatchWarning();
      return;
    }
    navigate(ROUTE.GROUP_SKILLS_PROFICIENCY, { state: { selectedEmployees: effectiveEmployees } });
  }, [navigate, getEffectiveSelection, haveSelectedEmployeesSameJob, showJobMismatchWarning]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SkillsBridgerContainerStyled vertical>
      <SkillsBridgerPageHeader
        onGroupSkillsMap={handleGroupSkillsMap}
        onGroupSkillsProficiency={handleGroupSkillsProficiency}
        loading={loading}
        hasSelection={selectedEmployees.length > 0}
      />
      <SkillsBridgerTableAreaStyled flex="1 1 auto">
        <SbSkillsBridgerHRV2
          filteredData={employeeData}
          loading={loading}
          selectedEmployees={selectedEmployees}
          onSelectionChange={setSelectedEmployees}
          onVisibleRowsChange={setVisibleEmployees}
        />
      </SkillsBridgerTableAreaStyled>
    </SkillsBridgerContainerStyled>
  );
};

export default SkillsBridgerV2;
